import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Sidebar } from '../../components/ui/sidebar';
import { WhatYouCanDo } from '../../components/ui/what-you-can-do/what-you-can-do';
import { Button } from '../../components/ui/button/button';
import {
  Bot,
  Plug,
  ChevronDown,
  ChevronUp,
  Copy,
  Edit,
  HelpCircle,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useAgents } from '../../contexts/AgentContext';
import { Agent, RagFile } from '../../lib/types';
import { useAiModels } from '../../lib/hooks/useAiModels';
import { format } from 'date-fns';
import { useSettingsStore } from '../../stores/settingsStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { Pill } from '../../components/pill/pill';
import HelpIcon from '../../components/ui/help-icon';
import agentBuilderData from '../../data/agentBuilder/data.json';
import InputFieldLabel from '../../components/ui/input-field-label';
import { DescriptionBox } from '../../components/ui/description-box';
import { FormAccordions } from '../../components/ui/form-accordions';
import { HideExamplesButton } from '../../components/ui/hide-button/hide-examples-button';
import { useGlobal } from '../../contexts/GlobalProvider';
import { EntityType } from '../../lib/enums';
import { CopyLinkButton } from '../../components/ui/copy-link-button/copy-link-button';
import { PageHeader } from '../../components/ui/page-header/page-header';
import { SectorPill } from '../../components/ui/sector-pill/sector-pill';
import { SectorSelect } from '../../components/ui/sector-select/sector-select';
import { useAuthStore } from '../../stores/authStore';
import {
  MODEL_SELECTION_TOOLTIP,
  TRIAL_DISABLED_TOOLTIP,
  TRIAL_MODEL,
} from '../../lib/trial-model';
import { useTutorialStore } from '../../stores/tutorialStore';
import {
  isManagedAgent,
  updateManagedAgentBudget,
} from '../../lib/managed-agents/api';
import { useManagedAgentImports } from '../../lib/hooks/useManagedAgentImports';
import { ManagedAgentDetails } from '../../components/ui/managed-agents/managed-agent-details';
import { ExternalAgentDetails } from '../../components/ui/connections/external-agent-details';
import { isConnectedAgent } from '../../lib/edge-functions';
import { isExternalAgent } from '../../lib/external-agents/api';
import { useExternalConnections } from '../../lib/hooks/useExternalConnections';

export const AgentBuilder = (): JSX.Element => {
  const { agentId: agentIdFromUrl } = useParams<{ agentId?: string }>();
  const navigate = useNavigate();
  const { hiddenExamples, toggleHiddenExample } = useGlobal();
  const { isActive: isOnboardingActive, markMilestoneComplete } =
    useTutorialStore();
  const { user: authUser } = useAuthStore();
  const isAdmin = authUser?.currentWorkspace?.role?.name === 'admin';
  const isTemplate =
    authUser?.currentWorkspace?.workspaces?.is_template ?? false;
  const {
    agents,
    createAgent,
    updateAgent,
    deleteAgent,
    uploadRagFile,
    getRagFiles,
    loading,
    error,
  } = useAgents();
  const { providerSettings } = useSettingsStore();
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const workspaceId = authUser?.currentWorkspace?.workspace_id;
  const { imports: managedImports, reload: reloadManagedImports } =
    useManagedAgentImports(workspaceId, agents);
  const { connections: externalConnections } = useExternalConnections(
    workspaceId,
    agents,
  );
  // Connected agents are configured outside PromptSafe: show a read-only
  // panel instead of the form.
  const isViewingConnectedAgent = isConnectedAgent(editingAgent);
  const [agentName, setAgentName] = useState('');
  const [shortTag, setShortTag] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(
    new Set(),
  );
  const [ragEnabled, setRagEnabled] = useState(false);
  const [ragFiles, setRagFiles] = useState<RagFile[]>([]);
  const [pendingRagFiles, setPendingRagFiles] = useState<
    { name: string; content: string }[]
  >([]);
  const [chunkSize, setChunkSize] = useState(1000);
  const [chunkOverlap, setChunkOverlap] = useState(100);
  const [topK, setTopK] = useState(3);
  const [embeddingModel, setEmbeddingModel] = useState(
    'text-embedding-3-large',
  );
  const [isAssistant, setIsAssistant] = useState(false);
  const [assistantId, setAssistantId] = useState('');
  const [assistantApiKey, setAssistantApiKey] = useState('');
  const [isCustomApi, setIsCustomApi] = useState(false);
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [customApiHeaders, setCustomApiHeaders] = useState('');
  const [agentPrompt, setAgentPrompt] = useState('');
  const [agentGreeting, setAgentGreeting] = useState<string | null>('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sectorId, setSectorId] = useState<string | null>(null);

  const isExampleAgentsHidden = hiddenExamples.includes(EntityType.Agents);

  const hasOpenAIKey = !!providerSettings['openai'];

  const { aiModels } = useAiModels();

  useEffect(() => {
    if (aiModels.length) {
      const defaultId = aiModels.find((m) => m.name === TRIAL_MODEL)?.id ?? '';
      setSelectedModel((prev) => prev || defaultId);
    }
  }, [aiModels]);
  const IntegrationInstructions = () => (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 text-sm font-semibold">Request body format:</h4>
        <pre className="overflow-x-auto rounded bg-gray-50 p-3 text-sm">
          {`{
  "messages": [
    {
        "role": "user",
        "content": "Hey! I'm Abigail, 26, and I work as an accountant. I'm here because I really want to lose weight effectively and sustainably. 😅 I've tried a few things, but I feel stuck, especially with my arthritis and stress. Just looking for some solid guidance!"
    }
  ]
}`}
        </pre>
      </div>
      <div>
        <h4 className="mb-2 text-sm font-semibold">
          Required Response Format:
        </h4>
        <pre className="overflow-x-auto rounded bg-gray-50 p-3 text-sm">
          {`{
  "response": "assistant's response message"
}

// Alternative fields that will be checked if the response property is not present:
{
  "message": "response text",  // or
  "content": "response text",  // or
  "text": "response text"
}`}
        </pre>
      </div>
    </div>
  );

  const applyAgentToForm = (agent: Agent) => {
    setEditingAgent(agent);
    setAgentName(agent.name);
    setShortTag(agent.short_tag || '');
    setSelectedModel(agent.model_id ?? '');
    setRagEnabled(agent.rag_enabled || false);
    setChunkSize(agent.chunk_size || 1000);
    setChunkOverlap(agent.chunk_overlap || 100);
    setTopK(agent.top_k || 3);
    setEmbeddingModel(agent.embedding_model || 'text-embedding-3-large');
    setIsAssistant(agent.is_assistant || false);
    setAssistantId(agent.assistant_id || '');
    setAssistantApiKey(agent.api_key || '');
    setIsCustomApi(agent.is_custom_api || false);
    setCustomApiUrl(agent.custom_api_url || '');
    setCustomApiHeaders(
      agent.custom_api_headers
        ? JSON.stringify(agent.custom_api_headers, null, 2)
        : '',
    );
    setPendingRagFiles([]);
    setAgentPrompt(agent.prompt || '');
    loadRagFiles(agent.id);
    setAgentGreeting(agent.greeting);
    setSectorId(agent.sector_id ?? null);
  };

  const canSelectAgent = (agent: Agent) =>
    !agent.is_example || isOnboardingActive;

  const handleEdit = (agent: Agent) => {
    if (agent.is_example && !isOnboardingActive) return;

    if (!agent.is_example) {
      applyAgentToForm(agent);
      navigate(`/agent-builder/${agent.id}`, { replace: true });
    }
    markMilestoneComplete('agentConfigured');
  };

  const handleClone = (agent: Agent) => {
    setEditingAgent(null);
    setRagFiles([]);
    setAgentName(`${agent.name} (Copy)`);
    setShortTag(agent.short_tag || '');
    setSelectedModel(agent.model_id ?? '');
    setRagEnabled(agent.rag_enabled || false);
    setChunkSize(agent.chunk_size || 1000);
    setChunkOverlap(agent.chunk_overlap || 100);
    setTopK(agent.top_k || 3);
    setEmbeddingModel(agent.embedding_model || 'text-embedding-3-large');
    setIsAssistant(agent.is_assistant || false);
    setAssistantId(agent.assistant_id || '');
    setAssistantApiKey(agent.api_key || '');
    setIsCustomApi(agent.is_custom_api || false);
    setCustomApiUrl(agent.custom_api_url || '');
    setCustomApiHeaders(
      agent.custom_api_headers
        ? JSON.stringify(agent.custom_api_headers, null, 2)
        : '',
    );
    setPendingRagFiles([]);
    setAgentPrompt(agent.prompt || '');
    setAgentGreeting(agent.greeting ?? '');
    setSectorId(agent.sector_id ?? null);
    navigate('/agent-builder', { replace: true });
    markMilestoneComplete('agentConfigured');
  };

  const loadRagFiles = async (agentId: string) => {
    try {
      const files = await getRagFiles(agentId);
      setRagFiles(files);
    } catch (error) {
      console.error('Error loading RAG files:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingAgent(null);
    setAgentName('');
    setShortTag('');
    setSelectedModel(aiModels.find((m) => m.name === TRIAL_MODEL)?.id ?? '');
    setRagEnabled(false);
    setChunkSize(1000);
    setChunkOverlap(100);
    setTopK(3);
    setEmbeddingModel('text-embedding-3-large');
    setIsAssistant(false);
    setAssistantId('');
    setAssistantApiKey('');
    setIsCustomApi(false);
    setCustomApiUrl('');
    setCustomApiHeaders('');
    setRagFiles([]);
    setPendingRagFiles([]);
    setAgentPrompt('');
    setAgentGreeting('');
    setSectorId(null);
    navigate('/agent-builder', { replace: true });
  };

  useEffect(() => {
    if (loading) return;
    if (!agentIdFromUrl) {
      return;
    }
    const agent = agents.find((a) => a.id === agentIdFromUrl);
    if (!agent || agent.is_example) {
      handleCancelEdit();
      return;
    }
    if (agent.id !== editingAgent?.id) {
      applyAgentToForm(agent);
    }
  }, [agentIdFromUrl, agents, loading]);

  const validateCustomApiConfig = () => {
    if (!customApiUrl) {
      throw new Error('Custom API URL is required');
    }

    if (customApiHeaders) {
      try {
        JSON.parse(customApiHeaders);
      } catch (e) {
        throw new Error('Invalid JSON format in custom headers');
      }
    }
  };

  const handleSave = async (status: 'published' | 'draft') => {
    if (!agentName.trim()) {
      toast.error('Please fill in the agent name');
      return;
    }

    // Check if agent name already exists in the agents list
    const nameExists = agents?.some(
      (agent) =>
        agent.name.toLowerCase() === agentName.toLowerCase().trim() &&
        agent.id !== editingAgent?.id,
    );

    if (nameExists) {
      toast.error(
        'An agent with this name already exists. Please choose another name.',
      );
      return;
    }

    if (!isAssistant && !isCustomApi && !agentPrompt.trim()) {
      toast.error('Please enter a prompt for the agent');
      return;
    }

    if (isAssistant) {
      if (!assistantId.trim()) {
        toast.error('Please provide an OpenAI Assistant ID');
        return;
      }
      if (!assistantApiKey.trim()) {
        toast.error('Please provide an OpenAI API key for the assistant');
        return;
      }
    }

    if (isCustomApi) {
      try {
        validateCustomApiConfig();
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : 'Invalid custom API configuration',
        );
        return;
      }
    }

    if (!isAssistant && !isCustomApi && ragEnabled && !hasOpenAIKey) {
      toast.error(
        'Please configure your OpenAI API key in settings before enabling RAG',
      );
      return;
    }

    setIsSaving(true);
    try {
      const agentData = {
        name: agentName,
        short_tag: shortTag ? shortTag : null,
        model_id: isAssistant || isCustomApi ? null : selectedModel,
        model: null,
        prompt: isAssistant || isCustomApi ? null : agentPrompt,
        rag_enabled: isAssistant || isCustomApi ? false : ragEnabled,
        rag_instructions: null,
        chunk_size: chunkSize,
        chunk_overlap: chunkOverlap,
        top_k: topK,
        embedding_model: embeddingModel,
        is_assistant: isAssistant,
        assistant_id: isAssistant ? assistantId : null,
        api_key: assistantApiKey ? assistantApiKey : null,
        is_custom_api: isCustomApi,
        custom_api_url: isCustomApi ? customApiUrl : null,
        custom_api_headers:
          isCustomApi && customApiHeaders
            ? (JSON.parse(customApiHeaders) as Record<string, string>)
            : null,
        custom_api_body_template: null,
        greeting: agentGreeting?.trim() || null,
        status,
        sector_id: isTemplate ? sectorId : null,
      };

      let savedAgentId: string | null = null;

      if (editingAgent) {
        const updatedAgent = await updateAgent(editingAgent.id, agentData);
        if (updatedAgent) {
          setEditingAgent(updatedAgent);
          savedAgentId = updatedAgent.id;
        }
      } else {
        const newAgent = await createAgent(agentData);
        savedAgentId = newAgent.id;

        if (ragEnabled && pendingRagFiles.length > 0) {
          for (const pendingFile of pendingRagFiles) {
            const file = new File([pendingFile.content], pendingFile.name, {
              type: 'text/plain',
            });
            await uploadRagFile(newAgent.id, file);
          }
        }

        setEditingAgent(newAgent);
        loadRagFiles(newAgent.id);
      }

      if (savedAgentId) {
        markMilestoneComplete('agentConfigured');
      }

      toast.success(status === 'draft' ? 'Draft saved' : 'Agent saved');

      if (savedAgentId) {
        navigate(`/agent-builder/${savedAgentId}`, { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save agent');
    } finally {
      setIsSaving(false);
      setPendingRagFiles([]);
    }
  };

  const handleDelete = async (agentId: string) => {
    if (confirm('Are you sure you want to delete this agent?')) {
      try {
        await deleteAgent(agentId);
        if (editingAgent?.id === agentId) {
          handleCancelEdit();
        }
        toast.success('Agent deleted');
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to delete agent',
        );
      }
    }
  };

  const togglePrompt = (agentId: string) => {
    setExpandedPrompts((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  };

  const filteredAgents = React.useMemo(() => {
    let results = agents;
    if (isExampleAgentsHidden) {
      results = results.filter((agent) => !agent.is_example);
    }

    if (!searchQuery.trim()) return results;

    const lower = searchQuery.toLowerCase();
    return results.filter(
      (agent) =>
        agent.name.toLowerCase().includes(lower) ||
        agent.short_tag?.toLowerCase().includes(lower) ||
        aiModels
          .find((m) => m.id === agent.model_id)
          ?.name.toLowerCase()
          .includes(lower),
    );
  }, [agents, searchQuery, isExampleAgentsHidden]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-prompt-safecomvulcan">Loading agents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex h-full w-full flex-col">
          <PageHeader
            title="Agent Builder"
            icon={
              <Bot className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />

          {!editingAgent && <WhatYouCanDo isAdmin={isAdmin} />}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 px-6 pt-5">
            <Button
              variant="outline"
              onClick={() => navigate('/connections')}
              className="border-prompt-safecomoutrageous-orange text-prompt-safecomoutrageous-orange hover:text-prompt-safecomoutrageous-orange"
            >
              <Plug className="h-4 w-4" />
              Connect an agent
            </Button>
            {editingAgent && (
              <Button
                onClick={handleCancelEdit}
                className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white hover:text-white"
              >
                Create new agent
              </Button>
            )}
          </div>

          <div className="flex gap-6 p-6 max-lg:flex-col">
            <div className="lg:w-1/2">
              <Card
                className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
                data-onboarding="agent-list"
              >
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
                    Existing agents
                  </CardTitle>
                  <HideExamplesButton
                    isHidden={isExampleAgentsHidden}
                    clickHandler={() => toggleHiddenExample(EntityType.Agents)}
                  />
                </CardHeader>
                <div className="pb-4 pl-[26px] pr-[30px] lg:w-[350px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-prompt-safecomhit-gray" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search agents..."
                      className="bg-prompt-safecomwhite indent-6"
                    />
                  </div>
                </div>
                <CardContent>
                  <div className="space-y-4">
                    {filteredAgents.map((agent) => (
                      <div
                        key={agent.id}
                        data-onboarding={
                          canSelectAgent(agent) ? 'agent-choice' : undefined
                        }
                        className={`rounded-lg border border-[#00000020] bg-prompt-safecomwhite p-4 ${
                          editingAgent?.id === agent.id
                            ? 'ring-2 ring-prompt-safecomoutrageous-orange'
                            : ''
                        } ${agent.is_example ? 'border-l-4 border-l-prompt-safecomoutrageous-orange' : ''} ${canSelectAgent(agent) ? 'cursor-pointer' : ''}`}
                        onClick={() =>
                          canSelectAgent(agent) && handleEdit(agent)
                        }
                        role={canSelectAgent(agent) ? 'button' : undefined}
                        tabIndex={canSelectAgent(agent) ? 0 : undefined}
                      >
                        <div className="mb-2 flex items-start justify-between">
                          <div>
                            <div className="flex max-md:flex-col md:items-center md:gap-2">
                              <h3 className="font-semibold text-prompt-safecomvulcan">
                                {agent.name}
                              </h3>
                              {agent.status === 'draft' && (
                                <Pill text="Draft" variant="default" />
                              )}
                              {agent.is_example && (
                                <span className="bg-prompt-safecomoutrageous-orange/10 rounded-full text-xs text-prompt-safecomoutrageous-orange md:px-2 md:py-0.5">
                                  Example
                                </span>
                              )}
                              {agent.is_assistant && (
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                                  OpenAI Assistant
                                </span>
                              )}
                              {agent.is_custom_api && (
                                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
                                  Custom API
                                </span>
                              )}
                              {isManagedAgent(agent) && (
                                <span className="whitespace-nowrap rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
                                  Claude Console
                                  {managedImports[agent.id] &&
                                    ` · v${managedImports[agent.id].anthropic_agent_version}`}
                                </span>
                              )}
                              {isExternalAgent(agent) && (
                                <span className="whitespace-nowrap rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
                                  Your API
                                </span>
                              )}
                              {agent.rag_enabled && (
                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                                  RAG Enabled
                                </span>
                              )}
                              {isTemplate && (
                                <SectorPill sectorId={agent.sector_id} />
                              )}
                            </div>
                            {!agent.is_assistant && !agent.is_custom_api && (
                              <p className="text-sm text-prompt-safecomabbey max-md:pt-1">
                                Model:{' '}
                                {aiModels.find((m) => m.id === agent.model_id)
                                  ?.name ?? agent.model}
                              </p>
                            )}
                            {agent.is_custom_api && (
                              <p className="text-sm text-prompt-safecomabbey">
                                API: {agent.custom_api_url}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {!agent.is_assistant &&
                              !agent.is_custom_api &&
                              agent.prompt &&
                              (agent.prompt?.length ?? 0) > 150 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePrompt(agent.id);
                                  }}
                                  tooltipContent={
                                    expandedPrompts.has(agent.id)
                                      ? agentBuilderData.collapsePromptButton
                                          ?.tooltip
                                      : agentBuilderData.expandPromptButton
                                          ?.tooltip
                                  }
                                >
                                  {expandedPrompts.has(agent.id) ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            {!agent.is_example && (
                              <CopyLinkButton
                                path={`/agent-builder/${agent.id}`}
                                tooltipText={
                                  agentBuilderData.linkAgentButton?.tooltip
                                }
                              />
                            )}
                            {!isConnectedAgent(agent) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClone(agent);
                                }}
                                tooltipContent={
                                  agentBuilderData.cloneAgentButton?.tooltip
                                }
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                            {!agent.is_example && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(agent);
                                  }}
                                  tooltipContent={
                                    agentBuilderData.editAgentButton?.tooltip
                                  }
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(agent.id);
                                  }}
                                  tooltipContent={
                                    agentBuilderData.deleteAgentButton?.tooltip
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {isConnectedAgent(agent) && (
                          <p className="text-sm text-prompt-safecomabbey">
                            Runs outside PromptSafe. The prompt stays on your
                            side.
                          </p>
                        )}
                        {!agent.is_assistant &&
                          !agent.is_custom_api &&
                          !isConnectedAgent(agent) && (
                            <div className="relative whitespace-pre-wrap text-sm text-prompt-safecomabbey">
                              {expandedPrompts.has(agent.id) ? (
                                agent.prompt
                              ) : (
                                <>
                                  {agent.prompt?.slice(0, 150)}
                                  {(agent.prompt?.length ?? 0) > 150 && '...'}
                                </>
                              )}
                              {(agent.prompt?.length ?? 0) > 150 &&
                                !expandedPrompts.has(agent.id) && (
                                  <div className="pointer-events-none absolute bottom-0 left-0 h-8 w-full bg-gradient-to-t from-white to-transparent" />
                                )}
                            </div>
                          )}
                        <p className="mt-2 text-xs text-prompt-safecomabbey">
                          Last modified:{' '}
                          {format(new Date(agent.updated_at), 'yyyy-MM-dd')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:w-1/2" data-onboarding="agent-form">
              {editingAgent && isExternalAgent(editingAgent) && (
                <ExternalAgentDetails
                  agent={editingAgent}
                  connection={externalConnections[editingAgent.id]}
                  onSaveGreeting={(greeting) =>
                    updateAgent(editingAgent.id, {
                      greeting: greeting || null,
                    })
                  }
                  onClose={handleCancelEdit}
                />
              )}
              {editingAgent && isManagedAgent(editingAgent) && (
                <ManagedAgentDetails
                  agent={editingAgent}
                  agentImport={managedImports[editingAgent.id]}
                  onSaveGreeting={(greeting) =>
                    updateAgent(editingAgent.id, {
                      greeting: greeting || null,
                    })
                  }
                  onSaveBudget={async (budgetCents) => {
                    if (!workspaceId) return;
                    await updateManagedAgentBudget(
                      workspaceId,
                      editingAgent.id,
                      budgetCents,
                    );
                    reloadManagedImports();
                  }}
                  onClose={handleCancelEdit}
                />
              )}
              {/* Connected agents have nothing to edit here; keep the form mounted but hidden. */}
              <Card
                className={`rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a] ${
                  isViewingConnectedAgent ? 'hidden' : ''
                }`}
              >
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
                    {editingAgent ? 'Edit agent' : 'Create new agent'}
                  </CardTitle>
                  {editingAgent && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={handleCancelEdit}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  <DescriptionBox text={agentBuilderData.introText.text} />
                  <p className="!mt-0 p-0 text-sm italic text-prompt-safecomabbey">
                    {agentBuilderData.introText.note}
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <InputFieldLabel
                        label={agentBuilderData.agentName.label}
                      />
                      <Input
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        placeholder={agentBuilderData.agentName.placeholder}
                        className="bg-prompt-safecomwhite"
                      />
                    </div>
                    <div className="space-y-2">
                      <InputFieldLabel
                        label={agentBuilderData.shortTag.label}
                        tooltip={agentBuilderData.shortTag.tooltip}
                      />
                      <Input
                        value={shortTag}
                        onChange={(e) => setShortTag(e.target.value)}
                        placeholder={agentBuilderData.shortTag.placeholder}
                        className="bg-prompt-safecomwhite"
                        maxLength={25}
                      />
                    </div>
                  </div>
                  {!isAssistant && !isCustomApi && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                          {agentBuilderData.agentPrompt.label}
                        </label>
                        <HelpIcon
                          content={agentBuilderData.agentPrompt.tooltip}
                        />
                      </div>
                      <Textarea
                        value={agentPrompt}
                        onChange={(e) => setAgentPrompt(e.target.value)}
                        placeholder={agentBuilderData.agentPrompt.placeholder}
                        className="min-h-[100px] bg-prompt-safecomwhite font-mono text-sm"
                      />
                    </div>
                  )}
                  {/* <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                        Use OpenAI Assistant
                      </label>
                      <Switch
                        checked={isAssistant}
                        onCheckedChange={(checked) => {
                          setIsAssistant(checked);
                          if (checked) {
                            setIsCustomApi(false);
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                        Use Custom API
                      </label>
                      <Switch
                        checked={isCustomApi}
                        onCheckedChange={(checked) => {
                          setIsCustomApi(checked);
                          if (checked) {
                            setIsAssistant(false);
                          }
                        }}
                      />
                    </div>
                  </div> */}

                  {isAssistant ? (
                    <>
                      <div className="space-y-2">
                        <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                          Assistant ID
                        </label>
                        <Input
                          value={assistantId}
                          onChange={(e) => setAssistantId(e.target.value)}
                          placeholder="Enter OpenAI Assistant ID"
                          className="bg-prompt-safecomwhite"
                        />
                      </div>
                    </>
                  ) : isCustomApi ? (
                    <>
                      <div className="mb-4 flex items-center gap-2">
                        <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                          API Integration Instructions
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              aria-label="API Integration Instructions"
                            >
                              <HelpCircle className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="right"
                            className="w-[600px] p-4"
                            sideOffset={10}
                          >
                            <IntegrationInstructions />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                          API URL
                        </label>
                        <Input
                          value={customApiUrl}
                          onChange={(e) => setCustomApiUrl(e.target.value)}
                          placeholder="Enter your API endpoint URL"
                          className="bg-prompt-safecomwhite"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="font-prompt-safe-com-semantic-label text-prompt-safecomvulcan">
                          Custom Headers (JSON)
                        </label>
                        <Textarea
                          value={customApiHeaders}
                          onChange={(e) => setCustomApiHeaders(e.target.value)}
                          placeholder={`{
  "Authorization": "Bearer your-api-key",
  "Custom-Header": "custom-value"
}`}
                          className="min-h-[120px] bg-prompt-safecomwhite font-mono text-sm"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <InputFieldLabel
                          label={agentBuilderData.greetingMessage.label}
                          tooltip={agentBuilderData.greetingMessage.tooltip}
                        />
                        <Input
                          value={agentGreeting ?? ''}
                          onChange={(e) => setAgentGreeting(e.target.value)}
                          placeholder={
                            agentBuilderData.greetingMessage.placeholder
                          }
                          className="bg-prompt-safecomwhite"
                        />
                      </div>
                      <div className="space-y-2">
                        <InputFieldLabel
                          label={agentBuilderData.llmModelSelection.label}
                          tooltip={MODEL_SELECTION_TOOLTIP}
                        />
                        <Select
                          value={selectedModel}
                          onValueChange={setSelectedModel}
                        >
                          <SelectTrigger className="w-full bg-prompt-safecomwhite">
                            <SelectValue placeholder="Select a model" />
                          </SelectTrigger>
                          <SelectContent>
                            {aiModels.map((model) => (
                              <SelectItem
                                key={model.id}
                                value={model.id}
                                disabled={!model.enabled}
                                title={
                                  !model.enabled
                                    ? TRIAL_DISABLED_TOOLTIP
                                    : undefined
                                }
                              >
                                <>
                                  {model.name}
                                  {!model.enabled && (
                                    <span className="absolute right-8 text-xs italic text-muted-foreground">
                                      Coming soon
                                    </span>
                                  )}
                                </>
                              </SelectItem>
                            ))}
                            <SelectSeparator />
                            <SelectItem
                              value="add-models"
                              disabled
                              title={TRIAL_DISABLED_TOOLTIP}
                            >
                              Add models
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {isTemplate && isAdmin && (
                    <SectorSelect value={sectorId} onChange={setSectorId} />
                  )}

                  <FormAccordions
                    tipsAccordion={agentBuilderData.tipsAccordion}
                    exampleAccordion={agentBuilderData.exampleAccordion}
                  />

                  <div className="flex justify-end gap-2 pt-4">
                    {editingAgent && (
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        className="rounded-[50px] border-prompt-safecomoutrageous-orange px-8 text-prompt-safecomoutrageous-orange hover:bg-prompt-safecomfantasy hover:text-prompt-safecomoutrageous-orange"
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                    )}
                    {(!editingAgent || editingAgent.status === 'draft') && (
                      <Button
                        onClick={() => handleSave('draft')}
                        variant="outline"
                        className="rounded-[50px] border-prompt-safecomabbey px-8 text-prompt-safecomabbey hover:bg-prompt-safecomfantasy"
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving draft...' : 'Save draft'}
                      </Button>
                    )}
                    <Button
                      data-onboarding="agent-save"
                      onClick={() => handleSave('published')}
                      className="hover:bg-prompt-safecomoutrageous-orange/90 rounded-[50px] bg-prompt-safecomoutrageous-orange px-8 text-prompt-safecomwhite"
                      disabled={isSaving}
                    >
                      {isSaving
                        ? 'Saving...'
                        : editingAgent?.status !== 'published'
                          ? 'Publish'
                          : 'Update agent'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
